import React from "react";
import { FormActions } from "../../components/common/FormActions";
import {
  createColumn,
  createViewAction,
  formatDateTime,
  numberFormat,
  beautify,
  createApproveAction,
  createRejectAction,
  truncateText,
} from "../../utils/helpers";
import { API_REVERSAL_REQUESTS } from "../../utils/apis";
import {
  IsHeadOfAccountant,
  IsManager,
  IsSuperUser,
} from "../../utils/permissions";
import { URL_REVERSAL_REQUESTS } from "../../utils/urls";
import { StatusTag } from "../../components/common/registerUtils";
import { PENDING } from "../../utils/constants";
import { Tooltip } from "antd";

export const useReversalRequests = () => {
  const isSupper = IsSuperUser();
  const isManager = IsManager();
  const isHeadOfAccountant = IsHeadOfAccountant();
  const tableColumns = () => [
    createColumn("transaction_car_number", true, "Car Number"),
    createColumn("advance_phone_number", true, "Phone Number"),
    createColumn("transaction_txn_type", true, "Transaction Type"),
    createColumn("transaction_amount", false, "Amount"),
    createColumn("amount_received", false, "Received Amount"),
    // createColumn("deducted_amount", false, "Deducted Amount"),
    createColumn("reason", true, "Reason"),
    {
      id: "status",
      header: "Status",
      filter: {
        type: "select",
        options: [
          { id: "pending", name: "Pending" },
          { id: "approved", name: "Approved" },
          { id: "rejected", name: "Rejected" },
        ],
      },
    },
    createColumn("requested_by_name", true, "Requested By"),
    createColumn("review_note", true, "Review Note"),
    createColumn("reviewed_by_name", true, "Reviewed By"),
    createColumn("requested_at", { type: "date" }, "Created At"),
    createColumn("actions", false),
  ];

  const tableData = (data: any) => {
    if (!data) return [];

    return data.map((item: any) => {
      const transactionTypeFormatted = beautify(item?.transaction_txn_type);
      return {
        ...item,
        transaction_amount: numberFormat(item?.transaction_amount || 0),
        amount_received: numberFormat(item?.amount_received || 0),
        reason: (
          <Tooltip title={item?.reason}>
            <div>{truncateText(item?.reason, 5)}</div>
          </Tooltip>
        ),
        // deducted_amount: numberFormat(item?.deducted_amount || 0),
        transaction_txn_type: beautify(item?.transaction_txn_type),
        status: <StatusTag status={item?.status} />,
        requested_by_name: item?.requested_by_name,
        reviewed_by_name: item?.reviewed_by_name,
        requested_at: formatDateTime(item?.requested_at),
        actions: (
          <FormActions
            actions={[
              createViewAction(URL_REVERSAL_REQUESTS, item?.id),
              ...((isManager || isSupper || isHeadOfAccountant) &&
              item?.status === PENDING
                ? [
                    createApproveAction(
                      `${API_REVERSAL_REQUESTS}${item?.id}/approve/`,
                      `reversal requests for ${transactionTypeFormatted} from ${item?.advance_phone_number}`,
                      true,
                      "review_note",
                      "POST",
                    ),
                    createRejectAction(
                      `${API_REVERSAL_REQUESTS}${item?.id}/reject/`,
                      `reversal requests for ${transactionTypeFormatted} from ${item?.advance_phone_number}`,
                      true,
                      "review_note",
                    ),
                  ]
                : []),
            ]}
          />
        ),
      };
    });
  };

  const viewData = (item: any) => {
    if (!item) {
      return {
        sections: [
          {
            title: "Error",
            fields: {
              message: "No data available",
            },
          },
        ],
      };
    }

    const txn = item?.transaction || {};

    return {
      sections: [
        {
          title: "Reversal Request Details",
          fields: {
            transaction_id: item?.id,
            transaction_type: beautify(txn?.txn_type),
            booking_number: txn?.booking_number,
            car_number: txn?.car_number,
            phone_number: txn?.advance_phone_number,
            amount: numberFormat(txn?.amount || 0),
            advance_balance: numberFormat(txn?.advance_balance || 0),

            reason: item?.reason,
            status: <StatusTag status={item?.status} />,

            requested_by:
              item?.requested_by?.full_name || item?.requested_by?.username,
            requested_at: formatDateTime(item?.requested_at),

            reviewed_by:
              item?.reviewed_by?.full_name || item?.reviewed_by?.username,
            review_note: item?.review_note,
            reviewed_at: formatDateTime(item?.reviewed_at),
            created_at: formatDateTime(item?.created_at),
            updated_at: formatDateTime(item?.updated_at),
          },
        },
      ],
    };
  };

  const activityFilter = "advance_reversals";
  const tableTitle = "Reversal Requests";

  return {
    tableData,
    tableColumns,
    tableTitle,
    viewData,
    activityFilter,
  };
};
